/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CircularProgress, Dialog, DialogContent } from "@mui/material";
import "../css/SelectProfilePicDialog.css";
import useWSMApi from "services/api/useWSMApi";
import { useMutation } from "@tanstack/react-query";
import { useStorage } from "services/StorageProvider";
import { CropArea, getCroppedImage } from "./GetCroppedImage";
import ImageCropper from "./ImageCropper";

interface SelectProfilePicDialogProps {
  open: boolean;
  onClose: () => void;
}

const SelectProfilePicDialog: React.FC<SelectProfilePicDialogProps> = ({
  open,
  onClose,
}) => {
  const MAX_SIZE_BYTES = 5 * 1024 * 1024;
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(
    null,
  );
  const [croppedImageBlob, setCroppedImageBlob] = useState<Blob | null>(null);
  const { updateProfilePic } = useWSMApi();
  const { updateUserProfileData } = useStorage();

  const handleCropComplete = useCallback(
    async (pixels: CropArea) => {
      if (previewUrl && pixels) {
        try {
          setCroppedAreaPixels(pixels);
          const croppedImageUrl = await getCroppedImage(previewUrl, pixels);
          // Convert URL back to Blob for upload
          const response = await fetch(croppedImageUrl);
          const blob = await response.blob();
          setCroppedImageBlob(blob);
        } catch (error) {
          console.error("Error cropping image:", error);
        }
      }
    },
    [previewUrl],
  );

  const updatePorfilePicMutation = useMutation({
    mutationFn: (data: any) => updateProfilePic(data),
    onSuccess: (response) => {
      updateUserProfileData(response);
      onClose();
    },
  });

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);
      setCroppedAreaPixels(null);
      setCroppedImageBlob(null);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  useEffect(() => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setCroppedAreaPixels(null);
    setCroppedImageBlob(null);
    if (galleryInputRef.current) galleryInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  }, [open]);

  const openGallery = () => galleryInputRef.current?.click();
  const openCamera = () => cameraInputRef.current?.click();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setSelectedFile(null);
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      alert("Please select an image smaller than 5 MB.");
      e.target.value = "";
      setSelectedFile(null);
      setPreviewUrl(null);
      return;
    }
    setSelectedFile(file);
    setCroppedImageBlob(null);
    setCroppedAreaPixels(null);
  };

  const handleSave = () => {
    const formData = new FormData();
    const fileToUpload = croppedImageBlob || (selectedFile as Blob);
    formData.append("file", fileToUpload);
    updatePorfilePicMutation.mutate(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        className: `${
          previewUrl
            ? "selected-profile-pic-dialog-container"
            : "select-profile-pic-dialog-container"
        }`,
      }}
      BackdropProps={{
        className: "select-profile-pic-dialog-backdrop",
      }}
    >
      <DialogContent sx={{ padding: 0, margin: 0 }}>
        {previewUrl ? (
          <div className="select-profile-pic-dialog-button-container">
            <div className="image-container">
              {/* <img src={previewUrl} alt="Profile preview" /> */}
              <ImageCropper
                previewUrl={previewUrl}
                onCropComplete={handleCropComplete}
              />
            </div>

            <div className="button-container">
              <button
                style={{ backgroundColor: "#C2B58C" }}
                onClick={handleSave}
              >
                {updatePorfilePicMutation?.isPending ? (
                  <CircularProgress size={30} color="inherit" />
                ) : (
                  "Save"
                )}
              </button>
              <div className="divider"></div>
              <button onClick={onClose}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            {/* gallery-preferred input */}
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            {/* camera-preferred input */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="user" // front camera; use "environment" for back
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            <div className="select-profile-pic-dialog-button-container">
              <button
                style={{ backgroundColor: "#C2B58C" }}
                onClick={openGallery}
              >
                Open Gallery
              </button>
              <div className="divider"></div>
              <button onClick={openCamera}>Camera</button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SelectProfilePicDialog;
